import { Link, useParams } from 'react-router';
import { MathText } from '../dvi/MathText';
import { findWork, getSolutionWorks, problemUrl } from '../data';
import type { SolutionProblem } from '../types/articles';
import { NotFound } from './NotFound';

function ProblemList({
  workKey,
  bookKey,
  problems,
}: {
  workKey: string;
  bookKey: string | null;
  problems: SolutionProblem[];
}) {
  return (
    <ul>
      {problems.map((problem) => (
        <li key={problem.path}>
          <Link to={problemUrl(workKey, bookKey, problem.path)}>
            <MathText
              text={problem.title}
              formulasUrl={`${problemUrl(workKey, bookKey, problem.path)}/meta.formulas.dvi`}
              pages={problem.metaPages}
            />
            の解答
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * 解答集の作品トップページ (/solutions/:work)。
 * 説明と巻ごとの問題一覧 (単巻作品は問題一覧を直接) を表示し、
 * 下部に他の作品へのナビゲーションを置く (他ページと同じ導線)。
 */
export function SolutionsWork() {
  const { work: workKey } = useParams<{ work: string }>();
  const work = findWork(workKey ?? '');
  if (!work) {
    return <NotFound />;
  }
  const hasProblems = work.problems.length > 0 || work.books.some((b) => b.problems.length > 0);
  return (
    <>
      <div className="hero">
        <p className="article-breadcrumb">
          <Link to="/math">数学</Link> / <Link to="/math/solutions">解答集</Link>
        </p>
        <h1>{work.title}</h1>
      </div>
      <section className="card">
        {work.description.map((paragraph, i) => (
          <p key={i}>{paragraph}</p>
        ))}
        {work.problems.length > 0 && (
          <ProblemList workKey={work.key} bookKey={null} problems={work.problems} />
        )}
        {work.books
          .filter((book) => book.problems.length > 0)
          .map((book) => (
            <div key={book.key}>
              <h3>{book.title}</h3>
              <ProblemList workKey={work.key} bookKey={book.key} problems={book.problems} />
            </div>
          ))}
        {!hasProblems && !work.wip && <p>準備中です。</p>}
      </section>
      <nav className="card" aria-label="解答集の他の作品">
        <h2>
          <Link to="/math/solutions">解答集</Link>の他の作品
        </h2>
        <ul className="content-links">
          {getSolutionWorks().map((other) =>
            other.key === work.key ? (
              <li key={other.key}>
                <span className="current" aria-current="page">
                  {other.title}
                </span>
              </li>
            ) : (
              <li key={other.key}>
                <Link to={`/solutions/${other.key}`}>{other.title}</Link>
              </li>
            ),
          )}
        </ul>
      </nav>
    </>
  );
}
